import { request } from './base.service';

const adminKey = process.env.REACT_APP_ADMIN_API_KEY || '';
const adminHeaders: Record<string, string> = adminKey
    ? { 'Content-Type': 'application/json', 'x-admin-key': adminKey }
    : { 'Content-Type': 'application/json' };

export interface AdminConfig {
    key: string;
    value: string;
    updatedAt: string;
}

export const getAdminConfig = (): Promise<{ config: AdminConfig[] }> =>
    request('/admin/config', { headers: adminHeaders });

export const getAdminConfigKey = (key: string): Promise<AdminConfig> =>
    request(`/admin/config/${encodeURIComponent(key)}`, { headers: adminHeaders });

export const updateAdminConfig = (key: string, value: string): Promise<AdminConfig> =>
    request(`/admin/config/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: adminHeaders,
        body: JSON.stringify({ value }),
    });

// ── AI Tool types ────────────────────────────────────────────────────────────

export interface AiToolEndpoint {
    method: string;
    path: string;
    bodyTemplate?: Record<string, unknown>;
    resultKey?: string;
}

export interface AiToolApiConfig {
    endpoints: AiToolEndpoint[];
    combineStrategy?: string;
}

export interface AiToolDbConfig {
    model: string;
    operation: 'findMany' | 'findUnique' | 'count';
    where?: Record<string, unknown>;
    select?: Record<string, unknown>;
    orderBy?: Record<string, unknown>;
    take?: number;
}

export interface AiToolMacroStep {
    toolName: string;
    inputMapping: Record<string, string>;
}

export interface AiToolMacroConfig {
    steps: AiToolMacroStep[];
}

export type AiToolType = 'handler' | 'api' | 'db' | 'macro';

export interface AiTool {
    id: string;
    name: string;
    description: string;
    inputSchema: Record<string, unknown>;
    handlerKey: string | null;
    type: AiToolType;
    config: AiToolApiConfig | AiToolDbConfig | AiToolMacroConfig | null;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface ToolHandlers {
    handlers: string[];
    toolTypes: string[];
    dbModels: string[];
    dbOperations: string[];
    apiPathPrefixes: string[];
}

// ── AI Tool service functions ────────────────────────────────────────────────

export const getTools = (): Promise<{ tools: AiTool[] }> =>
    request('/admin/tools', { headers: adminHeaders });

export const createTool = (data: {
    name: string;
    description: string;
    inputSchema: Record<string, unknown>;
    handlerKey?: string;
    type?: AiToolType;
    config?: AiToolApiConfig | AiToolDbConfig | AiToolMacroConfig | null;
    enabled?: boolean;
}): Promise<{ tool: AiTool }> =>
    request('/admin/tools', {
        method: 'POST',
        headers: adminHeaders,
        body: JSON.stringify(data),
    });

export const getToolHandlers = (): Promise<ToolHandlers> =>
    request('/admin/tools/handlers', { headers: adminHeaders });

export const getTool = (id: string): Promise<{ tool: AiTool }> =>
    request(`/admin/tools/${encodeURIComponent(id)}`, { headers: adminHeaders });

export const updateTool = (id: string, data: Partial<AiTool>): Promise<{ tool: AiTool }> =>
    request(`/admin/tools/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: adminHeaders,
        body: JSON.stringify(data),
    });

export const deleteTool = (id: string): Promise<{ deleted: boolean }> =>
    request(`/admin/tools/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: adminHeaders,
    });

export const testTool = (id: string, input: Record<string, unknown>, userid: string): Promise<{ result: Record<string, unknown> }> =>
    request(`/admin/tools/${encodeURIComponent(id)}/test`, {
        method: 'POST',
        headers: adminHeaders,
        body: JSON.stringify({ input, userid }),
    });

// ── Tool stats ───────────────────────────────────────────────────────────────

export interface ToolStatsSummary {
    tool?: string;
    total: string;
    successes: string;
    failures: string;
}

export interface ToolStatsError {
    tool?: string;
    errorCode: string;
    count: string;
}

export interface ToolStatsDuration {
    tool?: string;
    avgMs: string;
    p50Ms: string;
    p95Ms: string;
    maxMs: string;
}

export interface ToolStatsTrend {
    day: string;
    calls: string;
    failures: string;
}

export interface ToolStatsResponse {
    summary: ToolStatsSummary[];
    errors: ToolStatsError[];
    durations: ToolStatsDuration[];
}

export interface ToolStatsDetailResponse extends ToolStatsResponse {
    trend: ToolStatsTrend[];
}

export const getToolStats = (hours: number = 24): Promise<ToolStatsResponse> => {
    const params = hours !== 24 ? '?hours=' + hours : '';
    return request('/admin/tools/stats' + params, { headers: adminHeaders });
};

export const getToolStatsByName = (toolName: string, hours: number = 168): Promise<ToolStatsDetailResponse> => {
    const params = hours !== 168 ? '?hours=' + hours : '';
    return request(`/admin/tools/stats/${encodeURIComponent(toolName)}` + params, { headers: adminHeaders });
};
